/**
 * HTTP adapter for the site map admin endpoints.
 *
 * `/sites` is loaded once and `/health` is polled, so, like the reliability client, this
 * neither retries on 401 (getAccessToken already renews silently) nor tracks analytics.
 */

import { toSiteMapHealth, toSiteMapSites } from '../models/siteMapAdapter';
import type {
  SiteMapHealth,
  SiteMapHealthResponse,
  SiteMapSites,
  SiteMapSitesResponse,
} from '../models/siteMapModels';

export interface SiteMapApiConfig {
  readonly apiHost: string;
  readonly getAccessToken: () => Promise<string | null>;
}

export type SiteMapResult<T> =
  | { readonly status: 'ok'; readonly data: T }
  | { readonly status: 'forbidden' }
  | { readonly status: 'error'; readonly message: string };

async function getJson<Wire, Domain>(
  path: string,
  config: SiteMapApiConfig,
  toDomain: (body: Wire) => Domain
): Promise<SiteMapResult<Domain>> {
  const token = await config.getAccessToken();
  if (!token) {
    throw new Error('No access token available');
  }

  const response = await fetch(`${config.apiHost}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 403) {
    return { status: 'forbidden' };
  }

  if (!response.ok) {
    return { status: 'error', message: `API error: ${response.status}` };
  }

  const body: Wire = await response.json();
  return { status: 'ok', data: toDomain(body) };
}

/**
 * @throws Error when no access token is available, or on network failure
 */
export function fetchSiteMapSites(config: SiteMapApiConfig): Promise<SiteMapResult<SiteMapSites>> {
  return getJson<SiteMapSitesResponse, SiteMapSites>(
    '/admin-api/site-map/sites',
    config,
    toSiteMapSites
  );
}

/**
 * @throws Error when no access token is available, or on network failure
 */
export function fetchSiteMapHealth(
  config: SiteMapApiConfig
): Promise<SiteMapResult<SiteMapHealth>> {
  return getJson<SiteMapHealthResponse, SiteMapHealth>(
    '/admin-api/site-map/health',
    config,
    toSiteMapHealth
  );
}
